import { Modal } from '../../../components/modal/modal';
import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Spinner } from '../../../components/spinner/spinner';
import { UserAddress } from '../../../clients/quipli-service/user/user';
import { Field } from '../../../components/field/field';
import { UserAddressCreateRequest } from '../../../clients/quipli-service/user/user';
import { SelectForm } from '../../../components/select-form/select-form';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { toast } from '../../../util/toasts';
import { Phone } from '../../../components/field/phone';
import { PlacesAutocomplete } from '../../../components/field/places-autocomplete';

export const UserAddressForm = ({
  isOpen,
  onClose,
  onSubmit,
  userAddress,
  setUnsavedAddresses,
  title = 'User address setup',
  hideAddressType = false,
  hideSaveForFutureUse = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  userAddress: UserAddress;
  setUnsavedAddresses?: React.Dispatch<React.SetStateAction<UserAddress[]>>;
  title?: string;
  hideAddressType?: boolean;
  hideSaveForFutureUse?: boolean;
}) => {
  const { createUserAddress, updateUserAddress } = useAPI();

  const [saveAddressesByDefault, setSaveAddressesByDefault] = useState<boolean | undefined>(undefined);
  const [configsLoading, setConfigsLoading] = useState<boolean>(true);

  const { getConfiguration } = useCustomerAPI();

  useEffect(() => {
    getConfiguration('CONF_ADDRESSES_SAVED_BY_DEFAULT')
      .then((config) => {
        setSaveAddressesByDefault(config?.value === '1');
      })
      .then(() => setConfigsLoading(false));
  }, []);

  const [addressState, setAddressState] = useState<{
    id: number;
    userId: number;
    name: string;
    phone?: string;
    companyName?: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    isDeleted: boolean;
  }>({ ...userAddress });

  const addressSelectorType = [
    {
      value: 'both',
      label: 'Both',
    },
    {
      value: 'delivery',
      label: 'Delivery',
    },
    {
      value: 'billing',
      label: 'Billing',
    },
  ];

  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false),
    [isAddressGoogleAutogenerate, setAddressGoogleAutogenerate] = useState<boolean>(userAddress.isGoogleGenerated),
    [addressName, setAddressName] = useState<string>(userAddress.name),
    [addressPhone, setAddressPhone] = useState<string | undefined>(userAddress.phone),
    [addressPhoneIsValid, setAddressPhoneIsValid] = useState<boolean>(false),
    [addressCompany, setAddressCompany] = useState<string>(userAddress.companyName ?? ''),
    [secondAddress, setSecondAddress] = useState<string>(userAddress.address2 ?? ''),
    [addressType, setAddressType] = useState<{
      value: string;
      label: string;
    }>({
      value: hideAddressType ? userAddress.type : userAddress.id !== 0 ? userAddress.type : 'both',
      label:
        userAddress.id !== 0 || hideAddressType ? (userAddress.type === 'billing' ? 'Billing' : 'Delivery') : 'Both',
    });

  // Callbacks
  const submitUserAddress = async () => {
      if (addressPhone !== '' && !addressPhoneIsValid) {
        toast('Please enter a valid phone number', 'error');
        return;
      }

      // Block buttons and exit of modal
      setRequestInProgress(true);

      const body = {
        id: userAddress.id,
        userId: userAddress.userId,
        name: addressName,
        phone: addressPhone,
        companyName: addressCompany,
        address1: addressState.address1,
        address2: secondAddress,
        city: addressState.city,
        state: addressState.state,
        country: addressState.country,
        zip: addressState.zip,
        isGoogleGenerated: isAddressGoogleAutogenerate,
        type: addressType.value,
        isDeleted: !saveAddressesByDefault,
      };

      let body2: UserAddressCreateRequest | undefined = undefined;

      if (addressType.value === 'both') {
        body.type = userAddress.id === 0 ? 'delivery' : userAddress.type;

        body2 = {
          ...body,
          type: body.type === 'delivery' ? 'billing' : 'delivery',
        };
      }

      if (userAddress.id === 0) {
        await createUserAddress(body as UserAddressCreateRequest);
      } else {
        await updateUserAddress(userAddress.id, body as UserAddress);
      }

      if (body2 !== undefined) {
        await createUserAddress(body2 as UserAddressCreateRequest);
      }

      if (!saveAddressesByDefault && setUnsavedAddresses) {
        setUnsavedAddresses((old) => [...old, body as UserAddress]);
      }

      // Close modal
      setRequestInProgress(false);
      onSubmit?.();
    },
    isBtnDisabled = (): boolean => {
      return (
        isRequestInProgress ||
        addressName === '' ||
        addressState.address1 === '' ||
        addressState.state === '' ||
        addressState.city === '' ||
        addressState.country === '' ||
        addressState.zip === ''
      );
    },
    closeModal = () => {
      // Don't close modal if there is a request in progress
      if (isRequestInProgress) return;

      onClose();
    };

  return (
    <Modal
      title={title}
      actions={
        isRequestInProgress || configsLoading ? (
          <Spinner />
        ) : (
          <>
            <button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={() => closeModal()}
            >
              Cancel
            </button>

            <button
              className="button button-small button-block button-yellow"
              disabled={isBtnDisabled()}
              onClick={() => submitUserAddress()}
            >
              Save
            </button>
          </>
        )
      }
      isOpen={isOpen}
      close={closeModal}
    >
      <Field
        type="text"
        label="Full name"
        dataCy="full_name"
        value={addressName}
        onChange={(name: string) => {
          setAddressName(name);
        }}
        isRequired={true}
      />

      <Phone
        label="Phone number"
        dataCy="phone_number"
        value={addressPhone}
        onChange={setAddressPhone}
        onChangeValidity={setAddressPhoneIsValid}
      />

      <Field
        type="text"
        label="Company name"
        dataCy="company_name"
        value={addressCompany}
        onChange={(companyName: string) => {
          setAddressCompany(companyName);
        }}
        isRequired={false}
      />

      <PlacesAutocomplete
        label="Address line 1"
        value={addressState.address1}
        dataCy="google_address"
        onChange={(address1: string) => {
          setAddressState({ ...addressState, address1 });
        }}
        onPredictionSelected={(generatedAddress) => {
          setAddressState({
            ...addressState,
            address1: generatedAddress.address1,
            zip: generatedAddress.postcode !== '' ? generatedAddress.postcode : '-',
            city: generatedAddress.city !== '' ? generatedAddress.city : '-',
            state: generatedAddress.state !== '' ? generatedAddress.state : '-',
            country: generatedAddress.country !== '' ? generatedAddress.country : '-',
          });

          setAddressGoogleAutogenerate(true);
        }}
        isRequired={true}
      />
      <Field
        type="text"
        label="Address line 2"
        value={secondAddress}
        onChange={(address2: string) => {
          setSecondAddress(address2);
        }}
        isRequired={false}
      />
      <Field
        type="text"
        label="State/Province name"
        value={addressState.state}
        onChange={(state: string) => {
          setAddressState({ ...addressState, state });
        }}
        isRequired={true}
        disabled={isAddressGoogleAutogenerate}
        description="After setting Address Line 1 this will be automatically updated"
      />

      <Field
        type="text"
        label="City name"
        value={addressState.city}
        onChange={(city: string) => {
          setAddressState({ ...addressState, city });
        }}
        isRequired={true}
        disabled={isAddressGoogleAutogenerate}
        description="After setting Address Line 1 this will be automatically updated"
      />

      <Field
        type="text"
        label="Country name"
        value={addressState.country}
        onChange={(country: string) => {
          setAddressState({ ...addressState, country });
        }}
        isRequired={true}
        disabled={isAddressGoogleAutogenerate}
        description="After setting Address Line 1 this will be automatically updated"
      />

      <Field
        type="text"
        label="Postcode"
        value={addressState.zip}
        onChange={(zip: string) => {
          setAddressState({ ...addressState, zip });
        }}
        isRequired={true}
        disabled={isAddressGoogleAutogenerate}
        description="After setting Address Line 1 this will be automatically updated"
      />

      {!hideAddressType && (
        <div className="user-setup-field">
          <SelectForm
            label="Address type"
            options={addressSelectorType}
            value={addressType}
            getOptionValue={(option) => option.value}
            onChange={(type) =>
              setAddressType(
                type ?? {
                  value: 'both',
                  label: 'Both',
                },
              )
            }
          />
        </div>
      )}

      {!hideSaveForFutureUse && (
        <Checkbox
          name="Save address"
          label="Save address for future use"
          isChecked={saveAddressesByDefault}
          onChange={(value) => setSaveAddressesByDefault(value.valueOf())}
        />
      )}
    </Modal>
  );
};
